// Default content constants for CMS blocks — used as fallback when no CMS data exists

export const DEFAULT_POLICY_PRIVACY = {
  html: `<h1>Privacy Policy</h1><p><em>Last updated: May 2026</em></p><h2>1. Information We Collect</h2><p>When you create an account, we collect your email address and display name. When you use the Coach, we store your conversation history to improve your experience. We also collect basic analytics data such as page views and session duration.</p><h2>2. How We Use Your Information</h2><p>We use your information to provide and improve the Outspoken service, personalize your experience, communicate important updates, and process payments for premium features.</p><h2>3. AI Model Training — We Do Not Train On Your Data</h2><p>We do <strong>not</strong> use your conversations, messages, reflections, or any personal content to train, fine-tune, or improve any AI model — ours or a third party's. Your conversations are sent to our model providers strictly to generate the response you see, and our agreements with those providers prohibit them from training on your data. We do not share, sell, or license your conversation content to any third party for model training, advertising, or analytics.</p><h2>4. Data Storage &amp; Security</h2><p>Your data is stored securely using industry-standard encryption. Conversation data is associated with your account and can be deleted upon request.</p><h2>5. Cookies</h2><p>We use essential cookies for authentication and session management. No third-party advertising cookies are used.</p><h2>6. Your Rights</h2><p>You may request access to, correction of, or deletion of your personal data at any time by contacting us via the <a href="/contact">contact page</a>.</p><h2>7. Contact</h2><p>For privacy-related inquiries, reach out via the <a href="/contact">contact page</a>.</p>`,
  updated_at: "2026-05-09T00:00:00Z",
};

export const DEFAULT_POLICY_TOS = {
  html: `<h1>Terms of Service</h1><p><em>Last updated: July 2026</em></p><h2>1. Acceptance of Terms</h2><p>By accessing or using Outspoken, you agree to be bound by these Terms of Service. If you do not agree, please do not use the service.</p><h2>2. Description of Service</h2><p>Outspoken is an AI-powered clarity and decision-making companion inspired by Indian wisdom traditions. It is not a substitute for professional therapy, legal, financial, or medical advice.</p><h2>3. User Accounts</h2><p>You are responsible for maintaining the confidentiality of your account credentials. You must provide accurate information when creating an account.</p><h2>4. Free &amp; Paid Plans</h2><p>Outspoken offers both free and paid tiers. Free users receive 3 sessions per week, with access to all four lenses (Bhagavad Gita, Chanakya, Vidur, and Ramayan). Free sessions do not retain memory between conversations. Premium features require a paid subscription. We reserve the right to modify pricing and features.</p><h2>5. Acceptable Use</h2><p>You agree not to misuse the service, attempt to exploit the AI system, or use it for any illegal purpose. We reserve the right to suspend accounts that violate these terms.</p><h2>6. Limitation of Liability</h2><p>Outspoken is provided "as is" without warranties of any kind. We are not liable for any decisions made based on guidance provided by the AI personas.</p><h2>7. Contact</h2><p>Questions about these terms? Contact us at namaste@outspoken.co.in.</p>`,
  updated_at: "2026-07-01T00:00:00Z",
};

export const DEFAULT_POLICY_REFUND = {
  html: `<h1>Refund &amp; Cancellation Policy</h1><p><em>Last updated: February 2026</em></p><h2>1. Cancellation</h2><p>You may cancel your subscription at any time. Your access continues until the end of the current billing period.</p><h2>2. Refunds</h2><p>Refund requests are evaluated on a case-by-case basis. Contact namaste@outspoken.co.in within 7 days of your charge for consideration.</p><h2>3. No Partial Refunds</h2><p>We do not offer partial refunds for unused portions of a billing cycle.</p>`,
  updated_at: "2026-02-01T00:00:00Z",
};

export const DEFAULT_POLICY_COOKIE = {
  html: `<h1>Cookie Policy</h1><p><em>Last updated: February 2026</em></p><h2>Essential Cookies</h2><p>We use essential cookies for authentication and session management. These are required for the platform to function.</p><h2>No Tracking Cookies</h2><p>We do not use third-party advertising or tracking cookies.</p>`,
  updated_at: "2026-02-01T00:00:00Z",
};

export const DEFAULT_PRICING_PLANS = {
  plans: [
    {
      id: "clarity",
      name: "Clarity",
      tagline: "Best for occasional guidance and first-time users",
      monthly_price: 0,
      annual_price: 0,
      annual_saving_label: "",
      status: "live",
      features: [
        "3 clarity sessions per week",
        "All 4 lenses included — Bhagavad Gita, Chanakya, Vidur, Ramayan",
        "Structured guidance (clarify → frame → act)",
        "No memory between sessions",
      ],
      badge_text: "",
      cta_text: "Start Free",
      cta_subtext: "",
    },
    {
      id: "circle",
      name: "Outspoken Circle",
      tagline: "For people who face repeated decisions, pressure, or complexity",
      monthly_price: 499,
      annual_price: 399,
      annual_saving_label: "Save 20%",
      status: "active",
      features: [
        "Unlimited Sessions",
        "Session memory & continuity",
        "Follow-up questions within a Session",
        "Saved insights and Session history",
      ],
      badge_text: "Recommended",
      cta_text: "Join the Circle",
      cta_subtext: "Cancel anytime.",
    },
    {
      id: "advisory_board",
      name: "Outspoken Council",
      tagline: "For those who treat self-mastery as a discipline, not an occasional thought",
      monthly_price: 799,
      annual_price: 639,
      annual_saving_label: "Save 20%",
      status: "active",
      features: [
        "Everything in Outspoken Circle",
        "Prakriti Profile — your persistent decision profile, built from your Sessions",
        "Council Mode — all four lenses answer the same question",
        "Weekly Reflection Report — your growth, made visible",
        "Early access to new lenses and features",
      ],
      badge_text: "Most Powerful",
      cta_text: "Get Council",
      cta_subtext: "Cancel anytime.",
    },
  ],
};

export const DEFAULT_HOME_HERO = {
  headline: "When your mind is noisy, clarity matters more than answers.",
  subheadline: "India's oldest decision-making systems. Applied to your exact situation. Right now.",
  cta_text: "Start a Session",
  cta_url: "/onboarding",
  secondary_cta_text: "See How It Works",
};

export const DEFAULT_HOME_PERSONAS = {
  heading: "Multiple wisdom lenses — not one voice.",
  subheading: "",
  personas: [
    { name: "Bhagavad Gita lens", framework_tag: "Inner peace & life clarity", description: "Calm, compassionate guidance for stress, relationships, and purpose." },
    { name: "Chanakya", framework_tag: "Strategy & pragmatism", description: "Sharp, strategic counsel for power, money, and career moves." },
    { name: "Vidur", framework_tag: "Multi-perspective wisdom", description: "Deep, layered analysis weighing long-term consequences." },
    { name: "Ramayan lens", framework_tag: "Dharma & righteous action", description: "Grounded guidance rooted in duty, honor, and integrity." },
  ],
};

export const DEFAULT_HOME_TESTIMONIALS = {
  heading: "From people who came in stuck and left thinking clearly.",
  testimonials: [],
};

export const DEFAULT_HOME_NEWSLETTER = {
  headline: "The Council Speaks — once a week, in your inbox.",
  body_text: "What's inside: one decision-making framework from the Gita, Arthashastra, Ramayan, or Mahabharata, applied to a real modern dilemma — plus one prompt to think with this week. No fluff. No spam. Unsubscribe anytime.",
  placeholder_text: "your@email.com",
  cta_text: "Join the Council",
};

export const DEFAULT_HOME_FOOTER = {
  tagline: "Not a mental health service. For crisis support, please contact a licensed professional.",
  links: [
    { label: "Pricing", url: "/pricing" },
    { label: "Start a Session", url: "/onboarding" },
    { label: "Dojo", url: "/dojo" },
    { label: "Contact", url: "/contact" },
    { label: "Privacy Policy", url: "/privacy" },
    { label: "Terms of Service", url: "/terms" },
    { label: "Refund Policy", url: "/refund-policy" },
    { label: "Cookie Policy", url: "/cookie-policy" },
  ],
  copyright_text: "© 2026 Outspoken. All rights reserved.",
};

export const DEFAULT_HOME_DHARMAAI = {
  heading: "",
  body_copy: "",
  cta_text: "",
  cta_url: "",
};

export const DEFAULT_HOME_PRICING_BANNER = {
  heading: "Free to try. Deepens with continuity.",
  subheading: "Upgrade when you want unlimited Sessions, memory across Sessions, and your history saved.",
  cta_text: "See full Circle membership and session details",
  cta_url: "/pricing",
};
